import DateFnsUtils from "@date-io/date-fns";
import EventIcon from "@material-ui/icons/Event";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import { format, parse, isValid } from "date-fns";
import React, { useEffect, useRef, useState } from "react";
import { Button, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { BlobProvider } from "@react-pdf/renderer";
// import MedicalTestInvoicePdf from "../prints/MedicalTestInvoicePdf";
import ReceptionistProfile from "../../models/domain/ReceptionistProfile";
import utils from "../../Utils";
import useServiceList from "../../controllers/serviceMaster/useServices";
import MedicalTestInvoicePdf from "../prints/MedicalInvoicePdf";
import DownloadIcon from "@material-ui/icons/GetApp";
import PrintIcon from "@material-ui/icons/Print";
import { useSnackbarContext } from "../../contexts/Snackbar";

/* ================= TYPES ================= */

export type PaymentMode = "CASH" | "CARD" | "UPI" | "ONLINE";

export interface InvoiceRow {
  id: string; // row identity
  serviceId?: number; // selected master reference
  description: string;
  amount: string; // FIX: string so input can be cleared with backspace
  qty: number;
}

export interface MedicalInvoiceScreenProps {
  invoice?: any | null;

  invoiceDone?: boolean | null;
  onSave?: (payload: any) => Promise<boolean>;
  onCancel?: () => void;
  loading?: boolean;
}

/* ================= HELPERS ================= */

const INR = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

const uid = () => Math.random().toString(36).slice(2, 9);

const parseInvoiceDate = (dateStr?: string): Date | null => {
  if (!dateStr) return new Date();
  const parsed = parse(dateStr, "dd/MM/yyyy", new Date());
  return isValid(parsed) ? parsed : new Date();
};

/** Set to `true` to open already-issued invoices read-only (reprint / view saved data). */
const READ_ONLY_WHEN_INVOICE_ISSUED = false;

/* ================= COMPONENT ================= */

export default function MedicalInvoiceScreen({
  invoice,
  invoiceDone = false,
  onSave,
  onCancel,
  loading = false,
}: MedicalInvoiceScreenProps) {
  const { setSnackbar } = useSnackbarContext();
  const readOnly = READ_ONLY_WHEN_INVOICE_ISSUED && Boolean(invoiceDone);

  const { services, isLoading, refresh: refreshServices } = useServiceList();

  /* ---------- rows ---------- */
  const initialRows: InvoiceRow[] =
    invoice?.particulars?.map((p: any, i: number) => ({
      id: `p-${i}`,
      description: p.description ?? "",
      qty: 1,
      amount: String(p.amount ?? 0),
    })) ||
    invoice?.rows?.map((r: any) => ({ ...r, amount: String(r.amount ?? 0) })) ||
    [];

  const [rows, setRows] = useState<InvoiceRow[]>(initialRows);

  useEffect(() => {
    setRows(initialRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice]);

  const addRow = () =>
    setRows((p) => [...p, { id: uid(), description: "", qty: 1, amount: "" }]);

  const updateRow = (id: string, patch: Partial<InvoiceRow>) =>
    setRows((p) => p.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const deleteRow = (id: string) =>
    setRows((p) => p.filter((r) => r.id !== id));

  /* ---------- calculations ---------- */
  // FIX: Use string state so the field can be cleared with backspace
  const [discountAmt, setDiscountAmt] = useState<string>(
    String(invoice?.discount ?? 0),
  );

  const subtotal = rows.reduce(
    (s, r) => s + r.qty * (parseFloat(r.amount) || 0),
    0,
  );
  const discount = Math.max(
    0,
    Math.min(subtotal, parseFloat(discountAmt) || 0),
  );
  const netPayable = Math.max(0, subtotal - discount);
  const displaySubtotal = readOnly
    ? Number(invoice?.consultInvAmount ?? subtotal)
    : subtotal;
  const displayNetPayable = readOnly
    ? Number(invoice?.totalAmount ?? netPayable)
    : netPayable;

  /* ---------- payment (ALL ORIGINAL STATE RETAINED) ---------- */
  const [paymentMode, setPaymentMode] = useState<PaymentMode>(
    invoice?.paymentMode || "CASH",
  );

  // FIX: Use string state so the field can be cleared with backspace
  const [amountReceiving, setAmountReceiving] = useState<string>("");

  const [transactionNo, setTransactionNo] = useState<string>(
    invoice?.transactionId ?? "",
  );
  const [bankName, setBankName] = useState<string>(invoice?.bankName ?? "");
  const [cardType, setCardType] = useState<string>(
    invoice?.cardChequeType ?? "",
  );

  const [invoiceDate, setInvoiceDate] = useState<Date | null>(() =>
    parseInvoiceDate(invoice?.date),
  );

  useEffect(() => {
    setInvoiceDate(parseInvoiceDate(invoice?.date));
  }, [invoice?.date]);

  useEffect(() => {
    if (!readOnly || !invoice) return;
    setDiscountAmt(String(invoice.discount ?? 0));
    setPaymentMode((invoice.paymentMode as PaymentMode) || "CASH");
    setTransactionNo(invoice.transactionId ?? "");
    setBankName(invoice.bankName ?? "");
    setCardType(invoice.cardChequeType ?? "");
    setAmountReceiving(String(invoice.paidAmount ?? 0));
  }, [readOnly, invoice]);

  const previouslyPaid = invoice?.paidAmount ?? 0;

  const remaining = readOnly
    ? Math.max(0, Number(invoice?.pendingAmount ?? 0))
    : Math.max(
        0,
        netPayable - previouslyPaid - (parseFloat(amountReceiving) || 0),
      );

  const pdfTotals = readOnly
    ? {
        subtotal: Number(invoice?.consultInvAmount ?? subtotal),
        discountAmount: Number(invoice?.discount ?? discount),
        netPayable: Number(invoice?.totalAmount ?? netPayable),
        previouslyPaid: 0,
        amountReceivingNow: Number(invoice?.paidAmount ?? 0),
        remainingBalance: Number(invoice?.pendingAmount ?? 0),
      }
    : {
        subtotal,
        discountAmount: discount,
        netPayable,
        remainingBalance: remaining,
        amountReceivingNow: parseFloat(amountReceiving) || 0,
      };

  /* ---------- print ---------- */
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // printer detection + iframe ref
  function isActualDesktop(): boolean {
    const ua = navigator.userAgent.toLowerCase();
    const isWindows = /windows nt/.test(ua);
    const isMac = /macintosh/.test(ua);
    const isLinux = /linux(?! android)/.test(ua) && !/android/.test(ua);
    const isDesktopOS = isWindows || isMac || isLinux;
    const notMobileUA = !/iphone|ipad|ipod|android|mobile/.test(ua);

    if (!isDesktopOS || !notMobileUA) return false;

    if (
      typeof (navigator as any).userAgentData !== "undefined" &&
      (navigator as any).userAgentData.mobile
    )
      return false;

    const primaryPointerFine = window.matchMedia("(pointer: fine)").matches;
    const anyHover = window.matchMedia("(any-hover: hover)").matches;

    return primaryPointerFine && anyHover;
  }

  function printPDF(pdfUrl: string | URL | undefined) {
    if (!isActualDesktop()) {
      const w = window.open(pdfUrl as string, "_blank");
      if (w) {
        try {
          w.print();
        } catch (err) {
          console.error("Print failed", err);
        }
      }
      return;
    }

    if (iframeRef.current && pdfUrl) {
      iframeRef.current.src = pdfUrl as string;
      iframeRef.current.onload = function () {
        iframeRef.current?.contentWindow?.print();
      };
    } else {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = pdfUrl as string;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();

        setTimeout(() => {
          document.body.removeChild(iframe);
          try {
            URL.revokeObjectURL(pdfUrl as string);
          } catch {}
        }, 1500);
      };
    }
  }

  function mapToConsultationInvoicePayload() {
    const particulars = rows.map((r) => ({
      description: r.description,
      amount: r.qty * (parseFloat(r.amount) || 0),
      sSGstAmount: 0,
      cGstAmount: 0,
    }));

    return {
      appointmentId: invoice?.appointmentId ?? null,
      date: invoiceDate ? format(invoiceDate, "dd/MM/yyyy") : "",

      paymentMode,
      bankName: bankName || "",
      bankBranch: "",
      transactionId: transactionNo || "",
      chequeNo: "",
      cardChequeType: cardType || "",
      accountHolder: "",

      consultInvAmount: subtotal,
      consultInvSGstAmount: 0,
      consultInvCGstAmount: 0,

      totalAmount: netPayable,
      totalSSGstAmount: 0,
      totalCGstAmount: 0,

      discount: discount,
      paidAmount: parseFloat(amountReceiving) || 0,
      pendingAmount: remaining,

      particulars,
    };
  }

  if (loading) return <div className="p-4 text-center">Loading…</div>;
  if (!invoice) return <div className="p-4 text-center">No invoice</div>;

  /* ================= UI ================= */

  return (
    <div className="p-3 bg-white" style={{ margin: "0 auto" }}>
      {/* Header */}

      {/* Clinic */}
      <div className="text-center mb-3">
        <div className="fw-bold">{invoice?.clinicName}</div>
        <div className="text-primary small"></div>
        <div className="small text-muted">
          {[invoice?.clinicPhone, invoice?.clinicEmail].join(" | ")}
        </div>
      </div>

      {/* Patient */}
      <div className="bg-light rounded p-3 mb-3">
        <div className="fw-bold">{invoice?.patientName}</div>
        <div className="small text-muted">{invoice?.doctorName}</div>

        <div className="d-flex justify-content-between mt-2 small">
          <div>
            <div className="text-muted mb-1">Date</div>
            <div
              className="d-flex align-items-center"
              style={{ height: 30 }}
            >
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                  disableToolbar
                  disableFuture
                  disabled={readOnly}
                  format="dd/MM/yyyy"
                  value={invoiceDate}
                  onChange={(date) => setInvoiceDate(date)}
                  variant="inline"
                  margin="none"
                  autoOk
                  inputVariant="outlined"
                  placeholder="dd/MM/yyyy"
                  InputProps={{
                    style: {
                      fontSize: "inherit",
                      height: 30,
                      width: 168,
                      backgroundColor: "#fff",
                    },
                  }}
                  inputProps={{
                    readOnly: true,
                    "aria-label": "Invoice date",
                    style: {
                      fontSize: "inherit",
                      padding: "2px 8px",
                      cursor: "pointer",
                    },
                  }}
                  keyboardIcon={
                    <EventIcon fontSize="small" style={{ color: "#0d6efd" }} />
                  }
                  KeyboardButtonProps={{
                    "aria-label": "Open calendar",
                    style: { padding: 4 },
                  }}
                />
              </MuiPickersUtilsProvider>
            </div>
          </div>
          <div className="text-end">
            <div className="text-muted mb-1">Invoice #</div>
            <div
              className="d-flex align-items-center justify-content-end"
              style={{ height: 30 }}
            >
              {invoice.receiptNo}
            </div>
          </div>
        </div>
      </div>

      {/* Tests (SIMPLIFIED UI) */}
      <div className="mb-3">
        <div className="text-muted small mb-2">SERVICES</div>

        <div className="border rounded">
          {rows.map((r) => (
            <div
              key={r.id}
              className="d-flex gap-2 align-items-center px-2 py-2 border-bottom"
            >
              {readOnly ? (
                <>
                  <div className="flex-grow-1">{r.description || "—"}</div>
                  <div className="text-end" style={{ minWidth: 90 }}>
                    {INR.format(r.qty * (parseFloat(r.amount) || 0))}
                  </div>
                </>
              ) : (
                <>
                  <Form.Select
                    value={r.description ?? ""}
                    onChange={(e) => {
                      const selectedService = services?.find(
                        (s) => s.name === e.target.value,
                      );

                      if (!selectedService) return;

                      updateRow(r.id, {
                        serviceId: selectedService.id,
                        description: selectedService.name,
                        amount: String(selectedService.defaultPrice),
                      });
                    }}
                  >
                    <option value="">Select Service</option>
                    {services?.map((service) => (
                      <option key={service.name} value={service.name}>
                        {service.name}
                      </option>
                    ))}
                  </Form.Select>

                  <Form.Control
                    type="number"
                    min={0}
                    value={r.amount}
                    onChange={(e) =>
                      updateRow(r.id, { amount: e.target.value })
                    }
                    style={{ width: 90, textAlign: "right" }}
                    placeholder="0"
                  />

                  <span
                    style={{ cursor: "pointer", color: "#d32f2f" }}
                    onClick={() => deleteRow(r.id)}
                  >
                    ×
                  </span>
                </>
              )}
            </div>
          ))}

          {!readOnly && (
            <div
              className="text-center py-2 text-primary"
              style={{ cursor: "pointer" }}
              onClick={addRow}
            >
              + Add Service
            </div>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="bg-light rounded p-3 mb-3">
        <div className="d-flex justify-content-between">
          <div>Subtotal</div>
          <div>{INR.format(displaySubtotal)}</div>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-2">
          <div>Discount</div>
          {readOnly ? (
            <div>{INR.format(discount)}</div>
          ) : (
            <Form.Control
              type="number"
              min={0}
              value={discountAmt}
              onChange={(e) => setDiscountAmt(e.target.value)}
              style={{ width: 120, textAlign: "right" }}
              placeholder="0"
            />
          )}
        </div>

        <hr />

        <div className="d-flex justify-content-between fw-bold">
          <div>Total</div>
          <div>{INR.format(displayNetPayable)}</div>
        </div>
      </div>

      {/* PAYMENT — ORIGINAL LOGIC RETAINED */}
      <div className="card mb-3">
        <div className="card-body">
          <h6>Payment</h6>

          {readOnly ? (
            <>
              <div className="d-flex justify-content-between small mb-2">
                <span className="text-muted">Mode of payment</span>
                <span className="fw-semibold">{paymentMode}</span>
              </div>
              <div className="d-flex justify-content-between small mb-2">
                <span className="text-muted">Amount received</span>
                <span>{INR.format(Number(invoice?.paidAmount ?? 0))}</span>
              </div>
              {paymentMode !== "CASH" && transactionNo && (
                <div className="d-flex justify-content-between small mb-2">
                  <span className="text-muted">Transaction number</span>
                  <span>{transactionNo}</span>
                </div>
              )}
              {paymentMode === "CARD" && bankName && (
                <div className="d-flex justify-content-between small mb-2">
                  <span className="text-muted">Bank name</span>
                  <span>{bankName}</span>
                </div>
              )}
              {paymentMode === "CARD" && cardType && (
                <div className="d-flex justify-content-between small mb-2">
                  <span className="text-muted">Card type</span>
                  <span>{cardType}</span>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="d-flex gap-2 mb-2">
                {(["CASH", "UPI", "CARD", "ONLINE"] as PaymentMode[]).map(
                  (m) => (
                    <button
                      key={m}
                      className={`btn btn-sm ${
                        paymentMode === m
                          ? "btn-primary"
                          : "btn-outline-secondary"
                      }`}
                      onClick={() => setPaymentMode(m)}
                    >
                      {m}
                    </button>
                  ),
                )}
              </div>

              <Form.Label>Amount Received</Form.Label>
              <Form.Control
                type="number"
                value={amountReceiving}
                onChange={(e) => setAmountReceiving(e.target.value)}
                placeholder="0"
              />

              {paymentMode !== "CASH" && (
                <>
                  <Form.Label className="mt-2">Transaction Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={transactionNo}
                    onChange={(e) => setTransactionNo(e.target.value)}
                  />
                </>
              )}

              {paymentMode === "CARD" && (
                <>
                  <Form.Label className="mt-2">Bank Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                  />

                  <Form.Label className="mt-2">Card Type</Form.Label>
                  <Form.Select
                    value={cardType}
                    onChange={(e) => setCardType(e.target.value)}
                  >
                    <option value="">Select</option>
                    <option value="DEBIT">Debit</option>
                    <option value="CREDIT">Credit</option>
                  </Form.Select>
                </>
              )}
            </>
          )}

          <div
            className="mt-3 p-2"
            style={{ background: "#fff3cd", color: "#b45309" }}
          >
            <div className="d-flex justify-content-between">
              <div>Pending Balance</div>
              <div className="fw-bold">{INR.format(remaining)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer / Actions */}
      <div className="d-flex justify-content-between align-items-center">
        {/* Far left */}
        <Button variant="light" onClick={onCancel}>
          Cancel
        </Button>

        {/* Right-aligned group */}
        <BlobProvider
          document={
            <MedicalTestInvoicePdf
              invoice={invoice}
              invoiceDate={
                invoiceDate ? format(invoiceDate, "dd/MM/yyyy") : ""
              }
              services={rows.map((r, i) => ({
                srNo: i + 1,
                description: r.description,
                qty: r.qty,
                amount: parseFloat(r.amount) || 0,
              }))}
              totals={pdfTotals}
              paymentDetails={{
                modeOfPayment: paymentMode,
                transactionNo,
                bankName,
                cardType,
              }}
            />
          }
        >
          {({ blob, loading }) => (
            <div className="d-flex gap-2">
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Download PDF</Tooltip>}
              >
                <Button
                  variant="outline-secondary"
                  disabled={loading || !blob}
                  onClick={() => {
                    if (!blob) return;
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `invoice-${invoice?.receiptNo ?? "draft"}.pdf`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  <DownloadIcon fontSize="small" />
                </Button>
              </OverlayTrigger>

              {/* Print PDF */}
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Print Invoice</Tooltip>}
              >
                <Button
                  variant="outline-secondary"
                  disabled={loading || !blob}
                  onClick={async () => {
                    if (!blob) return;
                    const pdfUrl = URL.createObjectURL(blob);
                    if (!readOnly && onSave) {
                      const payload = mapToConsultationInvoicePayload();
                      const ok = await onSave(payload);
                      if (!ok) return;
                    }
                    printPDF(pdfUrl);
                  }}
                >
                  <PrintIcon fontSize="small" />
                </Button>
              </OverlayTrigger>

              {!readOnly && (
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Save Invoice</Tooltip>}
                >
                  <Button
                    variant="primary"
                    disabled={loading}
                    onClick={async () => {
                      if (onSave) {
                        const payload = mapToConsultationInvoicePayload();
                        const ok = await onSave(payload);
                        if (!ok) return;
                      }
                    }}
                  >
                    Save
                  </Button>
                </OverlayTrigger>
              )}
            </div>
          )}
        </BlobProvider>
      </div>

      <iframe ref={iframeRef} style={{ display: "none" }} />
    </div>
  );
}
